// Academy series detail — Netflix-style: hero, video player iframe, episode list,
// "next videos". Playback gated by active subscription (unless free_preview).
// i18n : dictionnaire inline COPY (FR/EN/ES/PT).
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Lock, Clock, Film, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { apiFetch } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const t = raw ? (JSON.parse(raw)?.token) : null;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

const COPY = {
  fr: {
    loading: "Chargement…",
    not_found: "Série introuvable", back_academy: "Retour Academy",
    back_all_series: "Toutes les séries",
    videos_label: (n) => `${n} vidéos`,
    free_preview: "Aperçu gratuit",
    next_h: "Suite de la série", episodes_h: "Épisodes",
    generic_video: (i) => `Vidéo ${i}`,
    aperçu: "Aperçu",
    no_video: "Aucune vidéo dans cette série",
    gated_h: "Abonnement Academy requis",
    gated_p: "Activez votre essai 7 jours gratuits pour débloquer toutes les vidéos.",
    trial_cta: "Essai 7 jours",
    cross_eyebrow: "Aller plus loin", cross_h: "Prolongez l'expérience",
    cross_shop_eye: "Boutique", cross_shop_t: "Offrez la carte de cette région", cross_shop_d: "Posters, encadrés, canvas — 30+ régions imprimées",
    cross_blog_eye: "Blog", cross_blog_t: "Articles & guides connexes", cross_blog_d: "Wine insights, terroirs, conseils dégustation",
    cross_atlas_eye: "App geoVINUM Atlas", cross_atlas_t: "Cartes interactives & hors-ligne", cross_atlas_d: "30 jours gratuits, Academy intégrée",
    hr: "h", min: "min",
  },
  en: {
    loading: "Loading…",
    not_found: "Series not found", back_academy: "Back to Academy",
    back_all_series: "All series",
    videos_label: (n) => `${n} videos`,
    free_preview: "Free preview",
    next_h: "Next in the series", episodes_h: "Episodes",
    generic_video: (i) => `Video ${i}`,
    aperçu: "Preview",
    no_video: "No video in this series",
    gated_h: "Academy subscription required",
    gated_p: "Activate your free 7-day trial to unlock every video.",
    trial_cta: "7-day trial",
    cross_eyebrow: "Go further", cross_h: "Extend the experience",
    cross_shop_eye: "Shop", cross_shop_t: "Gift the map of this region", cross_shop_d: "Posters, framed, canvas — 30+ regions printed",
    cross_blog_eye: "Blog", cross_blog_t: "Related articles & guides", cross_blog_d: "Wine insights, terroirs, tasting tips",
    cross_atlas_eye: "geoVINUM Atlas App", cross_atlas_t: "Interactive & offline maps", cross_atlas_d: "30 free days, Academy included",
    hr: "h", min: "min",
  },
  es: {
    loading: "Cargando…",
    not_found: "Serie no encontrada", back_academy: "Volver a Academy",
    back_all_series: "Todas las series",
    videos_label: (n) => `${n} vídeos`,
    free_preview: "Vista previa gratis",
    next_h: "Continuación de la serie", episodes_h: "Episodios",
    generic_video: (i) => `Vídeo ${i}`,
    aperçu: "Vista previa",
    no_video: "Ningún vídeo en esta serie",
    gated_h: "Suscripción a Academy necesaria",
    gated_p: "Activa tu prueba gratuita de 7 días para desbloquear todos los vídeos.",
    trial_cta: "Prueba 7 días",
    cross_eyebrow: "Ir más lejos", cross_h: "Prolonga la experiencia",
    cross_shop_eye: "Tienda", cross_shop_t: "Regala el mapa de esta región", cross_shop_d: "Pósters, enmarcados, canvas — 30+ regiones impresas",
    cross_blog_eye: "Blog", cross_blog_t: "Artículos y guías relacionados", cross_blog_d: "Wine insights, terruños, consejos de cata",
    cross_atlas_eye: "App geoVINUM Atlas", cross_atlas_t: "Mapas interactivos y offline", cross_atlas_d: "30 días gratis, Academy integrada",
    hr: "h", min: "min",
  },
  pt: {
    loading: "A carregar…",
    not_found: "Série não encontrada", back_academy: "Voltar à Academy",
    back_all_series: "Todas as séries",
    videos_label: (n) => `${n} vídeos`,
    free_preview: "Pré-visualização gratuita",
    next_h: "Continuação da série", episodes_h: "Episódios",
    generic_video: (i) => `Vídeo ${i}`,
    aperçu: "Pré-visualização",
    no_video: "Nenhum vídeo nesta série",
    gated_h: "Subscrição Academy necessária",
    gated_p: "Ative o seu teste gratuito de 7 dias para desbloquear todos os vídeos.",
    trial_cta: "Teste de 7 dias",
    cross_eyebrow: "Ir mais longe", cross_h: "Prolongue a experiência",
    cross_shop_eye: "Loja", cross_shop_t: "Ofereça o mapa desta região", cross_shop_d: "Posters, emoldurados, canvas — 30+ regiões impressas",
    cross_blog_eye: "Blog", cross_blog_t: "Artigos e guias relacionados", cross_blog_d: "Wine insights, terroirs, dicas de prova",
    cross_atlas_eye: "App geoVINUM Atlas", cross_atlas_t: "Mapas interativos e offline", cross_atlas_d: "30 dias grátis, Academy integrada",
    hr: "h", min: "min",
  },
};

const fmtDuration = (sec, c) => {
  if (!sec) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h ? `${h}${c.hr} ${m}${c.min}` : `${m} ${c.min}`;
};

const AcademySeriesPage = () => {
  const { slug } = useParams();
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data: d } = await apiFetch(`/api/academy/series/${slug}`, { headers: authHeaders() });
      if (cancelled) return;
      setData(ok ? d : { error: true });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const activeVideo = useMemo(() => data?.videos?.[activeIdx], [data, activeIdx]);
  const nextVideos = useMemo(() => (data?.videos || []).slice(activeIdx + 1, activeIdx + 4), [data, activeIdx]);

  if (loading) {
    return <Layout testId="academy-series-loading"><div className="min-h-[60vh] flex items-center justify-center text-[color:var(--gv-ink)]/55">{c.loading}</div></Layout>;
  }
  if (!data || data.error) {
    return (
      <Layout testId="academy-series-error">
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <div className="font-display text-2xl mb-3">{c.not_found}</div>
            <Link to="/academy" className="btn-primary">{c.back_academy} <ArrowLeft size={14} className="rotate-180" /></Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout testId={`academy-series-${slug}`}>
      <SEO title={`${data.title} — Academy geoVINUM`} description={data.description?.slice(0, 160)} />

      {/* Hero blue */}
      <section className="bg-[color:var(--gv-blue)] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-12">
          <Link to="/academy" className="inline-flex items-center gap-1.5 text-[13px] text-white/65 hover:text-white mb-5" data-testid="series-back">
            <ArrowLeft size={14} /> {c.back_all_series}
          </Link>
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-display text-3xl md:text-5xl leading-[1.1] mb-3" data-testid="series-title">{data.title}</h1>
              <div className="text-[13px] text-white/65 flex items-center gap-3 mb-4">
                <span className="flex items-center gap-1"><Film size={12} /> {c.videos_label(data.videos_count)}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {fmtDuration(data.total_length_sec, c)}</span>
                <span className="px-2 py-0.5 bg-white/10 rounded-[5px] uppercase text-[10px] tracking-widest">{data.level}</span>
              </div>
              <p className="text-[14px] md:text-[15px] text-white/85 leading-relaxed max-w-2xl" data-testid="series-description">{data.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Player + next videos */}
      <section className="bg-white pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <VideoPlayer video={activeVideo} c={c} />
            {activeVideo && (
              <div className="mt-4">
                <div className="font-display text-xl text-[color:var(--gv-ink)] mb-1" data-testid="active-video-title">{activeVideo.title || c.generic_video(activeIdx + 1)}</div>
                <div className="text-[12px] text-[color:var(--gv-ink)]/55 flex items-center gap-2">
                  <Clock size={11} /> {fmtDuration(activeVideo.length, c)}
                  {activeVideo.free_preview && (
                    <span className="ml-2 px-2 py-0.5 bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)] rounded-[5px] text-[10px] uppercase tracking-widest font-medium">{c.free_preview}</span>
                  )}
                </div>
              </div>
            )}

            {/* Next videos band */}
            {nextVideos.length > 0 && (
              <div className="mt-8" data-testid="next-videos">
                <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">{c.next_h}</div>
                <div className="grid grid-cols-3 gap-3">
                  {nextVideos.map((v, i) => (
                    <button key={v.guid} onClick={() => setActiveIdx(activeIdx + 1 + i)} className="group text-left bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden hover:border-[color:var(--gv-pink)] transition-colors" data-testid={`next-video-${v.guid}`}>
                      <div className="aspect-video bg-[color:var(--gv-mist)] relative overflow-hidden">
                        {v.thumbnail ? <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" /> : null}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play size={20} className="text-white" fill="currentColor" />
                        </div>
                        {!v.playable && <Lock size={12} className="absolute top-2 right-2 text-white" />}
                      </div>
                      <div className="p-2">
                        <div className="text-[12.5px] font-medium line-clamp-2">{v.title || c.generic_video(activeIdx + 2 + i)}</div>
                        <div className="text-[10.5px] text-[color:var(--gv-ink)]/55 mt-1">{fmtDuration(v.length, c)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Episode list sidebar */}
          <aside className="lg:col-span-4" data-testid="episodes-list">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-3">{c.episodes_h}</div>
            <div className="space-y-2 max-h-[80vh] overflow-y-auto pr-1">
              {(data.videos || []).map((v, i) => (
                <button
                  key={v.guid}
                  onClick={() => setActiveIdx(i)}
                  className={`w-full text-left flex items-start gap-3 p-3 rounded-[5px] border transition-colors ${i === activeIdx ? "bg-[color:var(--gv-pink)]/10 border-[color:var(--gv-pink)]" : "bg-white border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"}`}
                  data-testid={`episode-${i}`}
                >
                  <div className="text-[14px] font-display text-[color:var(--gv-ink)]/55 w-6 shrink-0 pt-0.5">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[color:var(--gv-ink)] line-clamp-2 mb-1">{v.title || c.generic_video(i + 1)}</div>
                    <div className="text-[10.5px] text-[color:var(--gv-ink)]/55 flex items-center gap-2">
                      <Clock size={10} /> {fmtDuration(v.length, c)}
                      {v.free_preview && <span className="text-[color:var(--gv-pink)]">· {c.aperçu}</span>}
                      {!v.playable && <Lock size={10} className="ml-auto" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* Cross-sell — netlinking vers Shop + Blog + Atlas */}
      <section className="py-12 bg-[color:var(--gv-mist)] border-t border-[color:var(--gv-line)]" data-testid="series-crosslinks">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{c.cross_eyebrow}</div>
            <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">{c.cross_h}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/shop" className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="series-crosslink-shop">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{c.cross_shop_eye}</div>
              <div className="font-display text-[16px] text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)] transition-colors">{c.cross_shop_t}</div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/65 mt-2">{c.cross_shop_d}</div>
            </Link>
            <Link to="/blog" className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="series-crosslink-blog">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{c.cross_blog_eye}</div>
              <div className="font-display text-[16px] text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)] transition-colors">{c.cross_blog_t}</div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/65 mt-2">{c.cross_blog_d}</div>
            </Link>
            <Link to="/atlas/magnum" className="group block bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 hover:border-[color:var(--gv-pink)] transition-colors" data-testid="series-crosslink-atlas">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-2">{c.cross_atlas_eye}</div>
              <div className="font-display text-[16px] text-[color:var(--gv-ink)] group-hover:text-[color:var(--gv-pink)] transition-colors">{c.cross_atlas_t}</div>
              <div className="text-[12.5px] text-[color:var(--gv-ink)]/65 mt-2">{c.cross_atlas_d}</div>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const VideoPlayer = ({ video, c }) => {
  if (!video) {
    return <div className="aspect-video bg-[color:var(--gv-mist)] rounded-[5px] flex items-center justify-center text-[color:var(--gv-ink)]/30">{c.no_video}</div>;
  }
  if (!video.playable || !video.iframe_url) {
    return (
      <div className="aspect-video bg-[color:var(--gv-ink)] rounded-[5px] flex flex-col items-center justify-center text-white p-6 text-center" data-testid="player-gated">
        <Lock size={28} className="mb-3 opacity-70" />
        <div className="font-display text-xl mb-2">{c.gated_h}</div>
        <div className="text-[13px] text-white/70 mb-5 max-w-md">{c.gated_p}</div>
        <a href="https://app.geovinum.io" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[color:var(--gv-pink)] hover:bg-[color:var(--gv-pink-dark)] px-5 py-2.5 rounded-[5px] text-[14px]">
          <Sparkles size={13} /> {c.trial_cta}
        </a>
      </div>
    );
  }
  return (
    <div className="aspect-video bg-black rounded-[5px] overflow-hidden" data-testid="player-iframe-wrap">
      <iframe
        src={video.iframe_url}
        title={video.title}
        loading="lazy"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="w-full h-full border-0"
      />
    </div>
  );
};

export default AcademySeriesPage;
